import type { ReportMedia, ReportSummary } from '@obras/shared';
import { formatCoordinate, formatDate, formatDateTime, formatDuration } from '@obras/shared';

import type { LightboxItem } from '../../components/ui/Lightbox.js';

/** Uma foto ou um vídeo da obra, com o relatório de onde veio. */
export interface ItemDoAcervo extends ReportMedia {
  readonly reportTitle: string;
  readonly visitDate: string;
  /** A data que ordena: a da foto, quando o aparelho a gravou; senão, a da visita. */
  readonly quando: string;
}

/**
 * O acervo visual da obra: foto e vídeo de todos os relatórios num lugar só.
 *
 * A data que ordena é a da foto, quando o aparelho a gravou, e só na falta
 * dela a da visita. Vinte fotos do mesmo relatório têm a mesma data de visita
 * e ficariam numa ordem qualquer; a do arquivo devolve a sequência real do
 * percurso pela obra. Partilhado pela aba Galeria e pelo painel do Mapa de
 * Obras, para as duas mostrarem as mesmas fotos na mesma ordem.
 */
export function montarAcervo(
  reports: readonly ReportSummary[],
  recentesPrimeiro = true,
): ItemDoAcervo[] {
  const itens = reports.flatMap((report) =>
    report.media
      .filter((item) => item.kind === 'imagem' || item.kind === 'video')
      .map((item) => ({
        ...item,
        reportTitle: report.title,
        visitDate: report.visitDate,
        quando: item.takenAt ?? report.visitDate,
      })),
  );

  return itens.sort((a, b) =>
    recentesPrimeiro ? b.quando.localeCompare(a.quando) : a.quando.localeCompare(b.quando),
  );
}

/** Os itens do visualizador, com a data, a duração e a coordenada como metadado. */
export function paraSlides(itens: readonly ItemDoAcervo[]): LightboxItem[] {
  return itens.map((item) => ({
    id: item.id,
    url: item.url,
    title: item.reportTitle,
    kind: item.kind === 'video' ? 'video' : 'imagem',
    thumbnailUrl: item.thumbnailUrl,
    meta: [
      formatDateTime(item.takenAt) !== '—'
        ? formatDateTime(item.takenAt)
        : formatDate(item.visitDate),
      item.durationSeconds ? formatDuration(item.durationSeconds) : null,
      item.latitude != null && item.longitude != null
        ? `${formatCoordinate(item.latitude, 'lat')} ${formatCoordinate(item.longitude, 'lng')}`
        : null,
    ].filter((line): line is string => Boolean(line)),
  }));
}
